type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface DesktopLogBridge {
  logs?: {
    write: (req: {
      level: LogLevel;
      scope: string;
      message: string;
      meta?: Record<string, unknown>;
    }) => Promise<void>;
  };
}

function getDesktopBridge(): DesktopLogBridge | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as { desktop?: DesktopLogBridge }).desktop ?? null;
}

function safeMeta(meta?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!meta) return undefined;
  try {
    return JSON.parse(JSON.stringify(meta)) as Record<string, unknown>;
  } catch {
    return { note: 'meta serialization failed' };
  }
}

async function writeDesktopLog(
  level: LogLevel,
  scope: string,
  message: string,
  meta?: Record<string, unknown>,
): Promise<void> {
  const bridge = getDesktopBridge();
  if (!bridge?.logs?.write) return;
  try {
    await bridge.logs.write({ level, scope, message, meta: safeMeta(meta) });
  } catch {
    // Never crash renderer due to logging transport.
  }
}

function writeConsole(
  level: LogLevel,
  scope: string,
  message: string,
  meta?: Record<string, unknown>,
): void {
  const prefix = `[forgedas-web][${scope}] ${message}`;
  if (level === 'error') console.error(prefix, meta ?? '');
  else if (level === 'warn') console.warn(prefix, meta ?? '');
  else if (level === 'debug') console.debug(prefix, meta ?? '');
  else console.info(prefix, meta ?? '');
}

async function emit(
  level: LogLevel,
  scope: string,
  message: string,
  meta?: Record<string, unknown>,
): Promise<void> {
  writeConsole(level, scope, message, meta);
  await writeDesktopLog(level, scope, message, meta);
}

export const appLogger = {
  debug: (scope: string, message: string, meta?: Record<string, unknown>) =>
    emit('debug', scope, message, meta),
  info: (scope: string, message: string, meta?: Record<string, unknown>) =>
    emit('info', scope, message, meta),
  warn: (scope: string, message: string, meta?: Record<string, unknown>) =>
    emit('warn', scope, message, meta),
  error: (scope: string, message: string, meta?: Record<string, unknown>) =>
    emit('error', scope, message, meta),
};

let globalErrorWired = false;

export function wireGlobalErrorLogging(): void {
  if (globalErrorWired || typeof window === 'undefined') return;
  globalErrorWired = true;

  window.addEventListener('error', (event) => {
    void appLogger.error('window', 'unhandled error', {
      message: event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    void appLogger.error('window', 'unhandled rejection', {
      reason:
        event.reason instanceof Error
          ? { message: event.reason.message, stack: event.reason.stack }
          : String(event.reason),
    });
  });
}
